import { useEffect, useRef, useState } from "react";

const stats = [
  {
    number: "60+",
    description: "We're active in over 60 markets worldwide — from megacities to emerging regions you might not expect.",
    color: "#0073E5",
    textColor: "text-white",
    icon: "ri-global-line",
  },
  {
    number: "400+",
    description: "Four hundred studies and counting. Each one taught us something new. We bring all of that to yours.",
    color: "#D4F419",
    textColor: "text-black",
    icon: "ri-bar-chart-line",
  },
  {
    number: "50+",
    description: "From scrappy startups to household-name brands — over 50 clients trust us to get it right.",
    color: "#F4971E",
    textColor: "text-black",
    icon: "ri-team-line",
  },
];

function StatCard({ stat, index }: { stat: typeof stats[0]; index: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.2 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`${stat.textColor} rounded-2xl p-8 md:p-10 transition-all duration-700 ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
      }`}
      style={{ backgroundColor: stat.color, transitionDelay: `${index * 120}ms` }}
    >
      <div className="w-12 h-12 flex items-center justify-center rounded-full bg-black/10 mb-6">
        <i className={`${stat.icon} text-2xl`}></i>
      </div>
      <p className="text-6xl md:text-7xl font-bold mb-4 leading-none">{stat.number}</p>
      <p className="text-sm md:text-base leading-relaxed opacity-80 max-w-xs">{stat.description}</p>
    </div>
  );
}

export default function StatsSection() {
  const titleRef = useRef<HTMLDivElement>(null);
  const [titleVisible, setTitleVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setTitleVisible(true); },
      { threshold: 0.2 }
    );
    if (titleRef.current) observer.observe(titleRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="bg-white py-24 px-6 md:px-16">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Left: sticky title */}
          <div
            ref={titleRef}
            className={`lg:sticky lg:top-32 transition-all duration-700 ${
              titleVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-black leading-tight mb-6">
              The numbers that tell our{" "}
              <strong
                className="underline decoration-4 underline-offset-4"
                style={{ textDecorationColor: "#D4F419" }}
              >
                story
              </strong>{" "}
              better than we can
            </h2>
            <p className="text-base text-gray-500 leading-relaxed max-w-md">
              We&apos;re not big on bragging — but these numbers are real, earned, and honestly kind of exciting. They&apos;re proof that good research, done right, adds up to something meaningful.
            </p>
          </div>

          {/* Right: stat cards */}
          <div className="space-y-5">
            {stats.map((stat, i) => (
              <StatCard key={stat.number} stat={stat} index={i} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
